import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  useGetProfile,
  useAiSummary,
  useUploadAvatar,
  useUpdateAvatar,
} from '../../hooks/useProfile';
import { Button, MarkdownRenderer, Spinner, Toaster } from '../../components/common';
import MyEventsCard from '../../components/events/MyEventsCard';
import { Edit, User, Mail, Calendar, Link as LinkIcon, CheckCircle, Clock } from 'iconoir-react';
import { routes } from '../../lib/routes';
import { PROFILE_KEY } from '../../utils/query-keys';

interface ProfileData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string | null;
  bio: string | null;
  objective: string | null;
  linkedin_profile: string | null;
  onboarding_status: string;
  created_at: string;
  role: {
    id: string;
    title: string;
  };
  UserDocument?: {
    url: string;
    title: string;
  };
}

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, checkStatus } = useAuth();
  const queryClient = useQueryClient();
  const { data: profileData, isLoading: isProfileLoading } = useGetProfile();
  const { data: summaryData, isLoading: isSummaryLoading } = useAiSummary();
  const { mutateAsync: uploadAvatar, isPending: isUploadingAvatar } =
    useUploadAvatar();
  const { mutateAsync: updateAvatar, isPending: isUpdatingAvatar } =
    useUpdateAvatar();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const profile = profileData?.result as ProfileData | undefined;
  const summary = summaryData?.result?.summary;
  const isAvatarBusy = isUploadingAvatar || isUpdatingAvatar;

  if (!user) return <div className='p-4'>Loading...</div>;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Jun-18 (a user V1 test, P2): clicking the avatar now opens the file picker
  // and uploads inline, instead of routing to Settings. uploadAvatar stores the
  // file and returns a URL; updateAvatar persists it on the user; checkStatus
  // refreshes the auth user and we invalidate the fullProfile query (ProfilePage
  // reads profile.avatar from useGetProfile, a different cache key than the one
  // useUpdateAvatar invalidates) so the new photo shows without a reload.
  const handleAvatarClick = () => {
    if (isAvatarBusy) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.custom(t => (
        <Toaster message='Please choose an image file.' type='error' id={t.id} />
      ));
      return;
    }
    try {
      const uploaded = await uploadAvatar(file);
      await updateAvatar({ url: uploaded.result.url });
      await checkStatus();
      queryClient.invalidateQueries({
        queryKey: [...PROFILE_KEY, 'fullProfile'],
      });
      toast.custom(t => (
        <Toaster
          message='Profile picture has been updated successfully'
          type='success'
          id={t.id}
        />
      ));
    } catch (error: any) {
      toast.custom(t => (
        <Toaster
          message={error?.message || 'Failed to update profile picture'}
          type='error'
          id={t.id}
        />
      ));
    }
  };

  return (
    <div className='max-w-4xl mx-auto p-4 sm:p-6'>
      {/* Profile Header Card */}
      <div className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6'>
        {/* Banner - slightly taller for better avatar positioning */}
        <div className='h-28 sm:h-36 bg-gradient-to-r from-primary-500 to-primary-600' />

        {/* Profile Info - adjusted spacing for smoother avatar transition */}
        <div className='px-4 sm:px-6 pb-6'>
          <div className='flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-16'>
            {/* Avatar - larger border and shadow for better visual separation */}
            <div className='flex-shrink-0'>
              {/* Jun-16 (a user V1 test, P2): the avatar itself is a click target
                  with a hover edit-pen, not only the Edit Profile button.
                  Jun-18 enhancement: clicking now opens the file picker and
                  uploads the photo inline (see handleAvatarFileSelect), instead
                  of routing to Settings. */}
              <div
                onClick={handleAvatarClick}
                className={`relative group w-fit ${isAvatarBusy ? 'cursor-wait' : 'cursor-pointer'}`}
                title='Change profile photo'
              >
                {profile?.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={`${user.first_name} ${user.last_name}`}
                    className='w-24 h-24 sm:w-32 sm:h-32 rounded-full border-[5px] border-white shadow-lg object-cover bg-white ring-1 ring-gray-100'
                  />
                ) : (
                  <div className='w-24 h-24 sm:w-32 sm:h-32 rounded-full border-[5px] border-white shadow-lg bg-primary-100 flex items-center justify-center ring-1 ring-gray-100'>
                    <span className='text-2xl sm:text-4xl font-bold text-primary-600'>
                      {getInitials(user.first_name, user.last_name)}
                    </span>
                  </div>
                )}
                {isAvatarBusy && (
                  <div className='absolute inset-0 flex items-center justify-center rounded-full bg-black/40'>
                    <Spinner size='sm' color='primary' />
                  </div>
                )}
                <span className='absolute bottom-1 right-1 bg-primary-500 text-white rounded-full p-1.5 shadow ring-2 ring-white opacity-90 group-hover:opacity-100 transition-opacity'>
                  <Edit className='w-3.5 h-3.5' />
                </span>
                <input
                  ref={avatarInputRef}
                  type='file'
                  accept='image/*'
                  onChange={handleAvatarFileSelect}
                  className='hidden'
                />
              </div>
            </div>

            {/* Name & Actions */}
            <div className='flex-1 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pt-2 sm:pt-0 sm:pb-2'>
              <div>
                <h1 className='text-xl sm:text-2xl font-bold text-heading-500'>
                  {user.first_name} {user.last_name}
                </h1>
                <div className='flex items-center gap-2 text-body-400 mt-1'>
                  <Mail className='w-4 h-4' />
                  <span className='text-sm'>{user.email}</span>
                </div>
              </div>
              <Button
                variant='outline'
                size='sm'
                onClick={() => navigate(routes.settings)}
                className='w-fit'
              >
                <Edit className='w-4 h-4' />
                Edit Profile
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Main Content - 2 columns */}
        <div className='lg:col-span-2 space-y-6'>
          {/* About / Bio Section: render only if a bio already exists.
              Jun-19 (a user V1 test): the empty-state "About (optional) / Add
              Bio" prompt was removed entirely (reviewer: the AI Summary already
              represents the user, so the prompt is unnecessary clutter).
              Existing bios still render so we never silently drop text a user
              already wrote. */}
          {profile?.bio && (
            <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6'>
              <h2 className='text-base font-semibold text-heading-500 mb-3'>About</h2>
              {isProfileLoading ? (
                <div className='flex justify-center py-4'>
                  <Spinner size='md' />
                </div>
              ) : (
                <p className='text-body-600 whitespace-pre-wrap'>{profile.bio}</p>
              )}
            </div>
          )}

          {/* AI Summary Section */}
          <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-base font-semibold text-heading-500'>AI Summary</h2>
              <Button
                variant='plain'
                size='sm'
                onClick={() => navigate(`${routes.settings}?tab=ai-summary`)}
              >
                <Edit className='w-4 h-4' />
                Edit
              </Button>
            </div>
            {isSummaryLoading ? (
              <div className='flex justify-center py-4'>
                <Spinner size='md' />
              </div>
            ) : summary ? (
              <MarkdownRenderer markdown={summary} />
            ) : (
              <p className='text-body-400 italic'>
                Your AI-generated professional summary will appear here after completing onboarding.
              </p>
            )}
          </div>

          {/* Objective Section */}
          {profile?.objective && (
            <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6'>
              <h2 className='text-base font-semibold text-heading-500 mb-3'>Primary Objective</h2>
              <div className='inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-full'>
                <CheckCircle className='w-5 h-5' />
                <span className='font-medium'>{profile.objective}</span>
              </div>
            </div>
          )}

          {/* Multi-event: per-event goals (view + edit). Self-hides for
              single-event users. */}
          <MyEventsCard />
        </div>

        {/* Sidebar - 1 column */}
        <div className='space-y-6'>
          {/* Account Info Card */}
          <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6'>
            <h2 className='text-base font-semibold text-heading-500 mb-3'>Account Info</h2>
            <div className='space-y-4'>
              {/* Member Since */}
              <div className='flex items-start gap-3'>
                <Calendar className='w-5 h-5 text-body-400 mt-0.5' />
                <div>
                  <p className='text-sm text-body-400'>Member since</p>
                  <p className='text-body-600 font-medium'>
                    {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Onboarding Status */}
              <div className='flex items-start gap-3'>
                <CheckCircle className='w-5 h-5 text-body-400 mt-0.5' />
                <div>
                  <p className='text-sm text-body-400'>Onboarding</p>
                  <p className='text-body-600 font-medium capitalize'>
                    {profile?.onboarding_status?.replace('_', ' ') || user.onboarding_status?.replace('_', ' ') || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Role — Jun-16 (a user V1 test, P3): the internal RBAC role
                  ("User") is meaningless to a consumer. Only show a real,
                  non-default role if one exists. */}
              {profile?.role?.title &&
                profile.role.title.toLowerCase() !== 'user' && (
                <div className='flex items-start gap-3'>
                  <User className='w-5 h-5 text-body-400 mt-0.5' />
                  <div>
                    <p className='text-sm text-body-400'>Role</p>
                    <p className='text-body-600 font-medium capitalize'>
                      {profile.role.title}
                    </p>
                  </div>
                </div>
              )}

              {/* LinkedIn */}
              {profile?.linkedin_profile && (
                <div className='flex items-start gap-3'>
                  <LinkIcon className='w-5 h-5 text-body-400 mt-0.5' />
                  <div>
                    <p className='text-sm text-body-400'>LinkedIn</p>
                    <a
                      href={profile.linkedin_profile}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary-500 hover:text-primary-600 text-sm break-all'
                    >
                      View Profile
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className='bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6'>
            <h2 className='text-base font-semibold text-heading-500 mb-3'>Quick Actions</h2>
            <div className='space-y-2'>
              <Button
                variant='outline'
                className='w-full justify-start'
                onClick={() => navigate(routes.settings)}
              >
                <User className='w-4 h-4' />
                Personal Information
              </Button>
              <Button
                variant='outline'
                className='w-full justify-start'
                onClick={() => navigate(`${routes.settings}?tab=ai-summary`)}
              >
                <Edit className='w-4 h-4' />
                Edit AI Summary
              </Button>
              <Button
                variant='outline'
                className='w-full justify-start'
                onClick={() => navigate(`${routes.settings}?tab=password`)}
              >
                <Clock className='w-4 h-4' />
                Change Password
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
